import { useId, useState } from "react";
import { glossaryEntry, type GlossaryKey } from "../../lib/glossary";

// A small ⓘ affordance that reveals a plain-language definition. Part of the
// "ANANSI, approachable" foundation (sub-project A).
//
// Two ways to use it:
//   <InfoTip termKey="pipeline" />          → pulls term + definition from the glossary
//   <InfoTip label="Market" text="..." />   → inline override for one-off copy
//
// Activation: opens on click/tap, on keyboard focus, and on mouse hover; closes
// on blur or Escape. State drives a class so touch devices (no :hover) can reach
// the text too. Accessibility: the trigger is a real <button> with an aria-label
// + aria-expanded; the bubble is role="tooltip" linked via aria-describedby.
// `termKey` is typed to the glossary keys, so a typo is a build error rather than
// a tip that silently fails closed.

interface InfoTipProps {
  /** Glossary key — looks up the term + definition (typo-checked at build time). */
  termKey?: GlossaryKey;
  /** Inline term label (use with `text`), overrides the glossary term. */
  label?: string;
  /** Inline definition (use with `label`), overrides the glossary definition. */
  text?: string;
}

export function InfoTip({ termKey, label, text }: InfoTipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const entry = termKey ? glossaryEntry(termKey) : undefined;
  const term = label ?? entry?.term;
  const definition = text ?? entry?.definition;

  // Fail safe: an unknown term (typo / not-yet-defined) renders nothing rather
  // than an empty bubble. In dev, surface it so we remember to add the entry.
  if (!definition) {
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`InfoTip: no glossary entry or text for termKey="${termKey}"`);
    }
    return null;
  }

  return (
    <span
      className="infotip"
      onKeyDown={(e) => {
        if (e.key === "Escape") setOpen(false);
      }}
    >
      <button
        type="button"
        className="infotip__btn"
        aria-label={term ? `What is ${term}?` : "What is this?"}
        aria-describedby={id}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
          <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.2" />
          <path d="M8 7.3v3.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <circle cx="8" cy="5" r="0.9" fill="currentColor" />
        </svg>
      </button>
      <span
        role="tooltip"
        id={id}
        className={`infotip__bubble${open ? " infotip__bubble--open" : ""}`}
      >
        {term && <span className="infotip__term">{term}</span>}
        <span className="infotip__def">{definition}</span>
      </span>
    </span>
  );
}
